import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Typography, Space, Avatar, Dropdown, Drawer } from 'antd';
import {
  DashboardOutlined, FileTextOutlined,
  MessageOutlined, FileSearchOutlined,
  LogoutOutlined, UserOutlined, HomeOutlined, MenuOutlined, CloseOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const pageTitles = {
  '/admin': { title: 'Tableau de bord', subtitle: 'Vue d\'ensemble de l\'activité' },
  '/admin/actualites': { title: 'Gestion des Actualités', subtitle: 'Publiez vos actualités' },
  '/admin/contacts': { title: 'Messages Reçus', subtitle: 'Consultez les demandes' },
  '/admin/devis': { title: 'Demandes de Devis', subtitle: 'Suivez les devis des clients' },
};

const AdminLayout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!user) navigate('/login');
    if (user && !isAdmin) navigate('/');
  }, [user, navigate, isAdmin]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const menuItems = [
    { key: '/admin', icon: <DashboardOutlined />, label: <Link to="/admin">Dashboard</Link> },
    { key: '/admin/actualites', icon: <FileTextOutlined />, label: <Link to="/admin/actualites">Actualités</Link> },
    { key: '/admin/contacts', icon: <MessageOutlined />, label: <Link to="/admin/contacts">Messages</Link> },
    { key: '/admin/devis', icon: <FileSearchOutlined />, label: <Link to="/admin/devis">Devis</Link> },
  ];

  const userMenuItems = [
    { key: 'home', icon: <HomeOutlined />, label: 'Retour au site', onClick: () => navigate('/') },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Déconnexion', onClick: () => { logout(); navigate('/login'); } },
  ];

  const currentPage = pageTitles[location.pathname] || pageTitles['/admin'];

  if (!user) return null;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={240}
        collapsedWidth={80}
        collapsible
        trigger={null}
        collapsed={sidebarCollapsed}
        className="admin-desktop-sider"
        style={{
          background: '#ffffff',
          borderRight: '1px solid #e9edf3',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'auto',
          zIndex: 120,
        }}
      >
        <Link to="/" style={{ display: 'block', padding: '20px 16px 18px', textAlign: 'center', borderBottom: '1px solid #eef1f6' }}>
          <img src="/img/logo-new.png" alt="Ndiouroul Voyages"
            style={{ height: sidebarCollapsed ? '42px' : '64px', width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
          {!sidebarCollapsed && (
            <Text style={{ color: '#8492a6', fontSize: '12px', fontWeight: '600', display: 'block', marginTop: '8px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
              Administration
            </Text>
          )}
        </Link>
        <div className="admin-sider-menu" style={{ marginTop: '10px' }}>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
          />
        </div>
        {!sidebarCollapsed && (
          <div style={{
            position: 'absolute', bottom: '20px', left: '16px', right: '16px',
            padding: '12px', background: '#f6f8fb',
            borderRadius: '12px', border: '1px solid #eef1f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Avatar size={32} style={{ background: '#00b894', flexShrink: 0 }} icon={<UserOutlined />} />
              <div style={{ minWidth: 0 }}>
                <Text style={{ color: '#132036', fontSize: '13px', fontWeight: '600', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.name}
                </Text>
                <Text style={{ color: '#8492a6', fontSize: '11px' }}>
                  Administrateur
                </Text>
              </div>
            </div>
          </div>
        )}
      </Sider>

      <Layout className="admin-main-layout" style={{ minWidth: 0 }}>
        <Header className="admin-header-bg" style={{
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          lineHeight: 'normal',
          height: 64,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
            <Button
              type="text"
              aria-label={sidebarCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'}
              icon={sidebarCollapsed
                ? <MenuUnfoldOutlined style={{ fontSize: '18px', color: '#1e3a5f' }} />
                : <MenuFoldOutlined style={{ fontSize: '18px', color: '#1e3a5f' }} />}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="admin-desktop-trigger"
            />
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: '18px', color: '#1e3a5f' }} />}
              onClick={() => setMobileMenuOpen(true)}
              style={{ display: 'none' }}
              className="admin-mobile-trigger"
            />
            <div style={{ minWidth: 0 }}>
              <Title level={4} style={{ margin: 0, color: '#1e3a5f', fontSize: '17px' }}>
                {currentPage.title}
              </Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {currentPage.subtitle}
              </Text>
            </div>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar style={{ background: '#00b894' }} icon={<UserOutlined />} />
              <Text style={{ fontSize: '13px' }}>{user.name}</Text>
            </Space>
          </Dropdown>
        </Header>

        <Content className="admin-content">
          <Outlet />
        </Content>
      </Layout>

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/img/logo-new.png" alt="Ndiouroul Voyages"
              style={{ height: '40px', width: 'auto', objectFit: 'contain' }} />
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={280}
        closeIcon={<CloseOutlined style={{ fontSize: '16px' }} />}
        styles={{ body: { padding: '12px' } }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{ border: 'none', fontSize: '15px' }}
        />
      </Drawer>

      <style>{`
        .admin-desktop-trigger { display: inline-flex; }
        .admin-mobile-trigger { display: none; }
        .admin-desktop-sider { display: block; }
        @media (max-width: 991px) {
          .admin-desktop-trigger { display: none !important; }
          .admin-mobile-trigger { display: inline-flex !important; }
          .admin-desktop-sider { display: none !important; }
        }
      `}</style>
    </Layout>
  );
};

export default AdminLayout;
